import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Bed, Bath, Home, Maximize, Info } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import PropertyStatusRibbon from "@/components/PropertyStatusRibbon";
import { resolveMediaUrl } from "@/utils/resolveMediaUrl";

const FeaturedPropertyCard = ({ property, index = 0, variant = "default" }) => {
  const { t } = useLanguage();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [showMobileInfo, setShowMobileInfo] = useState(false);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const images = property.images || [];
  const hasMultipleImages = images.length > 1;
  
  // Landscape variant for homepage featured section
  const isLandscape = variant === "landscape";

  const handlePrevImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const diff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(diff) > minSwipeDistance && hasMultipleImages) {
      if (diff > 0) {
        // Swipe left - next image
        setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
      } else {
        // Swipe right - previous image
        setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
      }
    }

    // Reset
    touchStartX.current = null;
    touchEndX.current = null;
  };

  // Toggle mobile info overlay
  const handleMobileInfoClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMobileInfo(!showMobileInfo);
  };

  // Format price with Swiss formatting
  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price).replace(/'/g, "'");
  };

  // Determine if overlay should show (hover on desktop, explicit toggle on mobile)
  const showOverlay = isHovered || showMobileInfo;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
    >
      <Link 
        to={`/properties/${property.id}`}
        className="block group"
        data-testid={`featured-property-${property.id}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Landscape variant: wider, more horizontal | Default: portrait 3/4 */}
        <div 
          className={`relative overflow-hidden media-frame ${
            isLandscape ? 'aspect-[16/10]' : 'aspect-[3/4]'
          }`}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img
            src={resolveMediaUrl(images[currentImageIndex] || images[0])}
            alt={property.title}
            className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
          />

          <PropertyStatusRibbon status={property.status} />
          
          {/* Mobile info button - only visible on touch devices */}
          <button
            onClick={handleMobileInfoClick}
            className="md:hidden absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm"
            aria-label="Show property info"
          >
            <Info size={16} className="text-[#1A1A1A]" strokeWidth={1.5} />
          </button>

          {/* Image counter for mobile */}
          {hasMultipleImages && (
            <div className="md:hidden absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
              {images.map((_, idx) => (
                <div 
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-colors ${
                    idx === currentImageIndex ? 'bg-white' : 'bg-white/40'
                  }`}
                />
              ))}
            </div>
          )}
          
          {/* Price overlay - hidden on hover/info */}
          <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent pt-16 pb-5 px-5 pointer-events-none transition-opacity duration-300 ${
            showOverlay ? 'opacity-0' : 'opacity-100'
          }`}>
            <p className="price-display font-normal inline-block text-white drop-shadow-lg bg-black/20 backdrop-blur-sm px-3 py-1 rounded-sm">
              CHF {formatPrice(property.price)}.-
            </p>
          </div>
          
          {/* Hover/Info overlay with description and property details */}
          <div className={`absolute inset-0 bg-[#1A1A1A]/85 transition-all duration-500 flex flex-col justify-end p-6 ${
            showOverlay ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}>
            {/* Close button for mobile */}
            {showMobileInfo && (
              <button
                onClick={handleMobileInfoClick}
                className="md:hidden absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center"
                aria-label="Close info"
              >
                <span className="text-white text-lg">×</span>
              </button>
            )}
            
            {/* Description section */}
            <div className="mb-4">
              <p className="text-white/70 text-[10px] uppercase tracking-[0.15em] mb-2">
                {t('propertyCard.description')}
              </p>
              <p className="text-white font-light text-sm leading-relaxed line-clamp-3">
                {property.short_description || property.description?.substring(0, 150) + '...'}
              </p>
            </div>
            
            {/* Price */}
            <div className="mb-4 pb-4 border-b border-white/20">
              <p className="price-display font-normal text-white">
                CHF {formatPrice(property.price)}.-
              </p>
            </div>
            
            {/* Bottom section - Property metrics with icons */}
            <div className="flex items-center justify-between">
              {/* Bedrooms */}
              <div className="flex flex-col items-center gap-1">
                <Bed size={18} className="text-white" strokeWidth={1.5} />
                <span className="text-white text-xs font-light">{property.bedrooms}</span>
              </div>
              
              {/* Bathrooms */}
              <div className="flex flex-col items-center gap-1">
                <Bath size={18} className="text-white" strokeWidth={1.5} />
                <span className="text-white text-xs font-light">{property.bathrooms}</span>
              </div>
              
              {/* Living Area */}
              <div className="flex flex-col items-center gap-1">
                <Home size={18} className="text-white" strokeWidth={1.5} />
                <span className="text-white text-xs font-light">{property.size_sqm} m²</span>
              </div>
              
              {/* Land Area (if available) */}
              {property.land_sqm > 0 && (
                <div className="flex flex-col items-center gap-1">
                  <Maximize size={18} className="text-white" strokeWidth={1.5} />
                  <span className="text-white text-xs font-light">{property.land_sqm} m²</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>
      
      {/* Image navigation arrows - below image, right aligned */}
      {hasMultipleImages && (
        <div className="flex justify-end gap-4 mt-3 mb-3">
          <button
            onClick={handlePrevImage}
            className="text-[#999] hover:text-[#666] transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeft size={18} strokeWidth={1} />
          </button>
          <button
            onClick={handleNextImage}
            className="text-[#999] hover:text-[#666] transition-colors"
            aria-label="Next image"
          >
            <ChevronRight size={18} strokeWidth={1} />
          </button>
        </div>
      )}
      
      {/* Spacer when no arrows */}
      {!hasMultipleImages && <div className="mb-4" />}
      
      {/* Property info below image - Subtle, understated hierarchy */}
      <Link to={`/properties/${property.id}`} className="block group">
        <p className="label-text mb-2">
          {property.location}
        </p>
        <h3 className="heading-secondary mb-2 group-hover:text-[#8B7355] transition-colors">
          {property.title}
        </h3>
        {/* Swiss format summary: rooms · surface · bedrooms */}
        <p className="body-text mt-1">
          {property.rooms && `${property.rooms} pièces · `}{property.size_sqm} m²{property.bedrooms && ` · ${property.bedrooms} ch.`}
        </p>
      </Link>
    </motion.div>
  );
};

export default FeaturedPropertyCard;
